import {faPencil} from "@fortawesome/pro-solid-svg-icons/faPencil";
import {faTag} from "@fortawesome/pro-solid-svg-icons/faTag";
import {faTrash} from "@fortawesome/pro-solid-svg-icons/faTrash";
import {deleteTags, GetItemCounts, TagUpdateType, useInfiniteTags, useUpdateTags} from "@twigsby/client-data";
import {
    Button,
    ColourSolid,
    ContentBlock,
    ContentLoader,
    FlexAlign,
    FlexJustify,
    InfiniteLoadTrigger, ItemRow, ItemRowHeader,
    StackHorizontal,
    ToggleSwitch,
    ToggleType,
    useComboBoxSimple,
    useLayout
} from "@twigsby/frontend-framework";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "@twigsby/frontend-framework";
import SelectEverythingBanner from "../../../Components/SelectEverythingBanner";
import SelectionBar from "../../../Components/SelectionBar";
import CreateTagModal from "../../../Components/Tags/Modals/CreateTagModal";
import DeleteTagModal from "../../../Components/Tags/Modals/DeleteTagModal";
import {useSite} from "../../../Providers/SiteInterfaceProvider";

const Tags = () => {
    const { t } = useTranslation();
    const { isXL } = useLayout();
    const { currentLocale, updatePageMeta } = useSite();

    const [ deleteTagOpen, setDeleteTagOpen ] = useState<boolean>(false);
    const [ createTagOpen, setCreateTagOpen ] = useState<boolean>(false);

    const [ editData, setEditData ] = useState<any>(null);

    const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 } );

    const {
        status,
        data,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
    } = useInfiniteTags();

    const {
        allSelected,
        visibleSelected,
        visibleIndeterminate,
        selectedItems,
        ToggleAll,
        ToggleVisible,
        ToggleSelected,
        ResetSelection,
    } = useComboBoxSimple();

    const updateTags = useUpdateTags();

    const tagContainerRef = useRef<any>();

    const HandleDelete = (tagId?: number) => {

        if(tagId) {
            deleteTags([tagId]).then(() => {
                updateTags.mutate({
                    newData: [tagId],
                    tagIds: [tagId],
                    updateType: TagUpdateType.DELETE
                })
            })
        } else {
            deleteTags(selectedItems).then(() => {
                updateTags.mutate({
                    newData: selectedItems,
                    tagIds: selectedItems,
                    updateType: TagUpdateType.DELETE
                })
            })

            ResetSelection();
           // ToggleVisible(data, 'id', !visibleSelected);
        }
    }

    const HandleEdit = (tagId: number, tagName: string) => {
        setEditData({ name: tagName, id: tagId});
        setCreateTagOpen(true);
    }

    const CreateModalClose = () => {
        setEditData(null);
        setCreateTagOpen(false);
    }

    const DeleteModalClose = () => {
        setEditData(null);
        setDeleteTagOpen(false);
        ResetSelection();
    }

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data as any));
        }
    }, [data]);

    useEffect(() => {
        updatePageMeta({
            title: t(`settings.sections.${"Tags"}`)
        });
    }, [currentLocale]);

    return (
        <>
            <StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between} extraClass={'mb-xs'}>
                <h2 className={'mb-0'}>{t(`settings.sections.${"Tags"}`)}</h2>
                <Button onClick={() => setCreateTagOpen(true)} title={t('settings.tags.actions.new')} squared={true} icon={faTag} colour={ColourSolid.Primary} />
            </StackHorizontal>

            <div className={'card tags-settings-rows'} ref={tagContainerRef}>

                <div className={`sticky ${status === 'loading' ? 'disabled' : ''}`}>

                    <ItemRowHeader sticky={true}>
                        <div className='item-row-checkbox'>
                            <ToggleSwitch
                                name={`tagsAll`}
                                label={`tags-all`}
                                invalid={false}
                                type={ToggleType.Checkbox}
                                defaultChecked={allSelected || !visibleIndeterminate && visibleSelected}
                                isIndeterminate={!allSelected && visibleIndeterminate}
                                onChange={(e: any) => ToggleVisible(data, 'id', !visibleSelected)}
                                hideLabel={true}
                            />
                        </div>

                        {selectedItems.length === 0 && (
                            <>
                                <div className='sm-sub-heading'>{t('settings.tags.headings.name')}</div>
                                <div className='sm-sub-heading'>{t('settings.tags.headings.count')}</div>
                                <div className='sm-sub-heading' />
                            </>
                        )}

                        {selectedItems.length > 0 && (
                            <SelectionBar selectedIds={selectedItems} clearSelectionCallback={() => ResetSelection()}>
                                <Button
                                    title={t('actions.delete')}
                                    icon={faTrash}
                                    iconOnly={!isXL}
                                    colour={ColourSolid.Alert}
                                    squared={true}
                                    onClick={() => setDeleteTagOpen(true)}
                                />
                            </SelectionBar>
                        )}
                    </ItemRowHeader>

                    {itemCounts.loaded !== itemCounts.total && selectedItems.length !== itemCounts.total && visibleSelected && (
                        <SelectEverythingBanner
                            selectedCount={allSelected ? itemCounts.total : selectedItems.length}
                            selectedLabel={allSelected ? t('actions.selectLoaded') : t('actions.selectAll')}
                            callback={() => ToggleAll(!allSelected)}
                        />
                    )}
                </div>

                {status === "loading" ? (
                    <ContentLoader childHeight={'5em'} childCount={10} />
                ) : status === "error" ? (
                    <ContentBlock error={true} title={error.name} content={error.message} />
                ) : (
                    <>
                        {data.pages.map((page, groupIndex: number) =>
                            page.data.map((tag, index: number) => {
                                if(tag.hidden) return null;

                                return (
                                    <ItemRow
                                        key={`tag${index}`}
                                        static={true}
                                        selected={allSelected || selectedItems.includes(tag.id)}
                                    >
                                        <div className='item-row-checkbox' onClick={(e) => e.stopPropagation()}>
                                            <ToggleSwitch
                                                name={`tagItem-${index}`}
                                                label={`tagItem-${index}`}
                                                invalid={false}
                                                type={ToggleType.Checkbox}
                                                defaultChecked={allSelected || selectedItems.includes(tag.id)}
                                                onChange={(e: any) => ToggleSelected(tag.id, e.currentTarget.checked)}
                                                hideLabel={true}
                                            />
                                        </div>
                                        <div>{tag.name}</div>
                                        <div>{tag.mailItemCount !== 0 && tag.mailItemCount}</div>
                                        <div className={'flex-row flex-gap-xs'}>
                                            <Button title={t('actions.edit')} onClick={() => HandleEdit(tag.id, tag.name)} colour={ColourSolid.Secondary} squared={true} iconOnly={true} icon={faPencil} />
                                        </div>
                                    </ItemRow>
                                )
                            })
                        )}

                        <InfiniteLoadTrigger
                            loaded={itemCounts.loaded}
                            totalCount={itemCounts.total}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />

                    </>
                )}
            </div>

            <CreateTagModal isOpen={createTagOpen} closeCallback={() => CreateModalClose()} actionedCallback={() => CreateModalClose()} existingData={editData} />
            <DeleteTagModal isOpen={deleteTagOpen} closeCallback={() => DeleteModalClose()} actionedCallback={() => DeleteModalClose()} selectedTags={selectedItems} />
        </>
    )
}

export default Tags
